import cn from 'classnames';
import { omit } from 'ramda';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { MedicalImageViewOptions } from '@/shared/api/protocol-ts/model/dto_common_image_view_options_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { ToggledToolNames, ToolNames } from '@/shared/config';
import { MedicalImageRender } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import type { Annotation } from '@/shared/graphics/RenderComponents/AnnotationsLayer/Annotation';
import { useAppDispatch, useAppSelector, useMedia } from '@/shared/hooks';
import { isNativeFullscreenSupported, trackEvent } from '@/shared/lib';
import {
  EnterFullscreenButton,
  ExitFromFullscreenButton,
  Icon,
  Tooltip,
} from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { reportsModel, transformViewOptions } from '@/entities/reports';
import {
  ToolbarID,
  toolbarMIRModel,
  useIsPseudoFullscreen,
} from '@/entities/toolbarMIR';
import { selectToolbarByID } from '@/entities/toolbarMIR/model/toolbarMIRSlice.selectors';

import { toothNumberEditModel } from '@/features/toothNumberEdit';

import type { ViewOptionsChangeEvent } from '../../config';
import {
  TOOL_NAME_TO_ICON_MAPPING,
  TOOLBAR_EVENT_MAPPING,
  toolsMessages,
} from '../../config';
import { useImagePropsSetters } from '../../hooks';
import { getToolbarControls, isCombinedTool, isToggledTool } from '../../utils';

import styles from './MedicalImageToolbar.module.scss';

type MedicalImageToolbarProps = {
  className?: string;
  toolbarID: ToolbarID;
  assetID?: string;
  toothID?: string;
  hasScale?: boolean;
  downloadFileName?: string;
  handleSetAnnotations?: (annotations: Annotation[]) => Promise<void>;
  handleSetMedicalImageViewOptions?: (
    viewOptions: MedicalImageViewOptions,
  ) => Promise<void>;
};

export const MedicalImageToolbar = (props: MedicalImageToolbarProps) => {
  const {
    className,
    toolbarID,
    assetID = '',
    toothID = '',
    hasScale,
    downloadFileName,
    handleSetAnnotations: handleSetAnnotationsOverride,
    handleSetMedicalImageViewOptions: handleSetMedicalImageViewOptionsOverride,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const { isMobile } = useMedia();
  const isPseudoFullscreen = useIsPseudoFullscreen();

  const toolbarState = useAppSelector(selectToolbarByID(toolbarID));
  const isMaskEditingEnabled = useAppSelector(
    organizationModel.selectors.selectFeatureFlag(
      FeatureFlag.FeatureFlag_Enable_MaskEditing,
    ),
  );

  const currentReport = useAppSelector(
    reportsModel.selectors.selectCurrentReport,
  );
  const activeTools = useAppSelector(
    toolbarMIRModel.selectors.selectActiveTools(toolbarID),
  );
  const { currentAngle, focusedImageMetaID, isTurnToolDisabled } =
    useAppSelector(reportsModel.selectors.selectRotatedImageMetaData);
  const imagePropsSetters = useImagePropsSetters(assetID, toothID);

  const handleSetAnnotations =
    handleSetAnnotationsOverride ?? imagePropsSetters.handleSetAnnotations;
  const handleSetMedicalImageViewOptions =
    handleSetMedicalImageViewOptionsOverride ??
    imagePropsSetters.handleSetMedicalImageViewOptions;

  const teethNumberPendingChanges = useAppSelector(
    toothNumberEditModel.selectors.selectPendingChanges,
  );

  const [shouldEditNumbersBlink, setShouldEditNumbersBlink] = useState(false);

  const reportType = currentReport?.Type;
  const isInvert = currentReport?.MedicalImageFeatures?.ViewOptionsV2?.Invert;
  const showPanoramaSplitInsteadOfSingle =
    currentReport?.Settings?.ShowPanoramaSplitInsteadOfSingle;
  const isFullscreenActive = activeTools.includes('expand');

  const controls = getToolbarControls({
    toolbarID,
    reportType,
    hasScale,
    isMobile,
    isMaskEditingEnabled,
  });

  const isIconActive = (control: ToolNames) => {
    return activeTools.includes(control as ToggledToolNames);
  };

  const controlClick = useCallback(
    (control: ToolNames, isActive: boolean) => {
      // NOTE: To avoid repetitive code, we dispatch the action in one place for combined and toggled tools.
      // 'expand' is excluded: its state is driven by the fullscreenchange listener,
      // so it only becomes active when the browser actually enters fullscreen
      if (
        (isCombinedTool(control) || isToggledTool(control)) &&
        control !== 'expand'
      ) {
        dispatch(
          toolbarMIRModel.actions.activateTool({
            toolbarID,
            control,
            value: !isActive,
          }),
        );
      }

      if (toolbarID === 'main' && !isActive) {
        const eventName = TOOLBAR_EVENT_MAPPING[control];

        if (eventName) {
          trackEvent({
            eventName,
          });
        }
      }

      switch (control) {
        case 'expand':
          // NOTE: iPhone Safari has no Element.requestFullscreen, so the report
          // render is stretched over the viewport with CSS instead; the expand
          // state is flipped here directly because fullscreenchange never fires.
          // Mirrors the native path: only the 'main' toolbar may expand.
          if (!isNativeFullscreenSupported()) {
            if (toolbarID === 'main') {
              dispatch(
                toolbarMIRModel.actions.expand({ toolbarID, value: !isActive }),
              );
            }
            break;
          }

          if (isActive) {
            document.exitFullscreen();
          } else {
            document
              .getElementById('report_render')
              ?.requestFullscreen()
              .catch(() => {});
          }
          break;

        case 'invert':
          dispatch(
            reportsModel.thunks.setReportMedicalImageViewOptions({
              ReportID: currentReport?.ID as string,
              MedicalImageViewOptions: new MedicalImageViewOptions({
                ...(currentReport?.MedicalImageFeatures
                  ?.ViewOptions as MedicalImageViewOptions),
                Invert: !isActive,
              }),
            }),
          );
          break;

        case 'split':
          dispatch(
            reportsModel.thunks.setReportSettings({
              ReportID: currentReport?.ID as string,
              ShowPanoramaSplitInsteadOfSingle: !isActive,
            }),
          );
          break;

        case 'turn':
          if (isTurnToolDisabled && control === 'turn') {
            break;
          }

          if (
            focusedImageMetaID &&
            currentReport?.ID &&
            currentAngle !== undefined
          ) {
            const newAngle = (currentAngle - 90) % 360;

            dispatch(
              reportsModel.thunks.setImageMetaOrientationAngle({
                ReportID: currentReport.ID,
                ImageMetaID: focusedImageMetaID,
                OrientationAngle: newAngle,
              }),
            );
          }
          break;

        case 'editNumbers':
          break;

        case 'reset':
          dispatch(
            toolbarMIRModel.actions.setToolbarState({
              toolbarID,
              state: {
                split: false,
                invert: false,
                expand: isFullscreenActive,
                hideMarks: false,
                activeControl: undefined,
                editNumbers: false,
              },
            }),
          );

          dispatch(toothNumberEditModel.actions.resetState());

          if (toolbarID === 'sliceEditor') {
            dispatch(
              toolbarMIRModel.actions.statelessAction({
                toolbarID: 'sliceEditor',
                toolName: 'reset',
                action: () => {
                  handleSetAnnotations([]);
                  handleSetMedicalImageViewOptions(
                    new MedicalImageViewOptions({
                      Brightness: 0,
                      Contrast: 0,
                      Sharpness: 0,
                    }),
                  );
                },
              }),
            );
          }

          if (toolbarID === 'main') {
            dispatch(
              reportsModel.thunks.setReportMedicalImageViewOptions({
                ReportID: currentReport?.ID as string,
                MedicalImageViewOptions: {
                  ...(isInvert && { Invert: false }),
                } as MedicalImageViewOptions,
              }),
            );
            dispatch(
              reportsModel.thunks.setReportSettings({
                ReportID: currentReport?.ID as string,
                ShowPanoramaSplitInsteadOfSingle: false,
              }),
            );
          }
          MedicalImageRender.resetUndoStack(toolbarID);
          break;

        case 'download':
          dispatch(
            toolbarMIRModel.actions.statelessAction({
              toolbarID,
              toolName: 'download',
              fileName: downloadFileName,
            }),
          );
          break;

        default:
          break;
      }
    },
    [
      toolbarID,
      currentReport,
      isFullscreenActive,
      focusedImageMetaID,
      currentAngle,
      isTurnToolDisabled,
      isInvert,
      downloadFileName,
      handleSetAnnotations,
      handleSetMedicalImageViewOptions,
    ],
  );

  const handleViewOptionsChange = useCallback(
    (event: ViewOptionsChangeEvent) => {
      const viewOptions = transformViewOptions(
        omit(['id', 'type', 'meta'], event),
      ) as MedicalImageViewOptions;

      switch (event.type) {
        case 'sharpnessChanged':
        case 'brightnessContrastChanged': {
          if (currentReport?.Type === ReportType.ReportType_CBCT_GP) {
            dispatch(
              reportsModel.thunks.setReportMedicalImageViewOptions({
                ReportID: currentReport?.ID,
                MedicalImageViewOptions: viewOptions,
              }),
            );
          } else {
            dispatch(
              reportsModel.thunks.setImageMetaViewOptions({
                ReportID: currentReport?.ID,
                ImageMetaID: event.meta?.imageMetaID as string,
                MedicalImageViewOptions: viewOptions,
              }),
            );
          }
          break;
        }

        case 'screenSharpnessChanged':
        case 'screenBrightnessContrastChanged': {
          dispatch(
            reportsModel.thunks.setReportMedicalImageViewOptions({
              ReportID: currentReport?.ID as string,
              MedicalImageViewOptions: viewOptions as MedicalImageViewOptions,
            }),
          );
          break;
        }
      }
    },
    [currentReport?.ID, currentReport?.Type, dispatch],
  );

  useEffect(() => {
    if (!toolbarState) {
      const initialState =
        toolbarID === 'main'
          ? {
              invert: !!isInvert,
              split: !!showPanoramaSplitInsteadOfSingle,
            }
          : undefined;

      dispatch(
        toolbarMIRModel.actions.initToolbarMIR({
          toolbarID: toolbarID,
          initialState,
        }),
      );
    }
  }, [toolbarID, toolbarState, isInvert, showPanoramaSplitInsteadOfSingle]);

  useEffect(() => {
    return () => {
      dispatch(toolbarMIRModel.actions.deleteToolbarMIR(toolbarID));
    };
  }, []);

  useEffect(() => {
    if (!toolbarState?.editNumbers && teethNumberPendingChanges.length) {
      setShouldEditNumbersBlink(true);
    } else {
      setShouldEditNumbersBlink(false);
    }
  }, [toolbarState?.editNumbers, teethNumberPendingChanges.length]);

  // set tools
  useEffect(() => {
    if (currentReport?.ID && toolbarID === 'main') {
      const disposers = [
        MedicalImageRender.addEventListener(
          'brightnessContrastChanged',
          handleViewOptionsChange,
        ),
        MedicalImageRender.addEventListener(
          'sharpnessChanged',
          handleViewOptionsChange,
        ),
        MedicalImageRender.addEventListener(
          'screenSharpnessChanged',
          handleViewOptionsChange,
        ),
        MedicalImageRender.addEventListener(
          'screenBrightnessContrastChanged',
          handleViewOptionsChange,
        ),
      ];

      return () => disposers.forEach((disposer) => disposer && disposer());
    }
  }, [currentReport?.ID, handleViewOptionsChange, toolbarID]);

  // NOTE: The fullscreenchange listener is the single source of the expand state:
  // it activates the tool only when the browser actually enters fullscreen and
  // deactivates it on any exit, including ESC
  useEffect(() => {
    const handleFullScreenChange = () => {
      // the fullscreen element is always the main report render, so only the
      // 'main' toolbar may become expanded; other toolbars only get reset
      const isMainToolbarFullscreen =
        toolbarID === 'main' && Boolean(document.fullscreenElement);

      dispatch(
        toolbarMIRModel.actions.expand({
          toolbarID,
          value: isMainToolbarFullscreen,
        }),
      );
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [toolbarID]);

  // The CSS fullscreen fallback is a fixed layer inside a scrollable page, so
  // the page behind it is locked while the layer is shown; the native mode
  // needs none of this because the browser takes over the whole viewport
  useEffect(() => {
    if (!isPseudoFullscreen || toolbarID !== 'main') {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isPseudoFullscreen, toolbarID]);

  return (
    <>
      {(!isMobile || isFullscreenActive) && (
        <div
          className={cn(styles.container, className)}
          data-section="medical-image-toolbar"
        >
          {controls.map((control) => {
            const isActive = isIconActive(control);

            return (
              <Tooltip.Primary
                key={control}
                side="right"
                content={
                  <span className="p3">
                    {formatMessage(toolsMessages[control])}
                  </span>
                }
              >
                <button
                  type="button"
                  data-component={`image-tool-${control}`}
                  aria-label={formatMessage(toolsMessages[control])}
                  className={cn(
                    styles.button,
                    isActive && styles.active,
                    isMobile && styles.mobileButton,
                    control === 'editNumbers' &&
                      shouldEditNumbersBlink &&
                      styles.blinking,
                  )}
                  disabled={isTurnToolDisabled && control === 'turn'}
                  onClick={() => controlClick(control, isActive)}
                >
                  <Icon
                    name={TOOL_NAME_TO_ICON_MAPPING[control]}
                    size={isMobile ? 42 : 32}
                  />
                </button>
              </Tooltip.Primary>
            );
          })}
        </div>
      )}

      {isMobile && !isFullscreenActive && (
        <EnterFullscreenButton
          className={cn(styles.standaloneFullscreenButton, styles.rtl)}
          onClick={() => controlClick('expand', isFullscreenActive)}
        />
      )}

      {isFullscreenActive && (
        <ExitFromFullscreenButton
          className={styles.standaloneFullscreenButton}
          onClick={() => controlClick('expand', isFullscreenActive)}
        />
      )}
    </>
  );
};
